import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import { 
  Calendar, 
  MapPin, 
  AlertCircle, 
  Package, 
  Truck,
  Users,
  Square,
  ChevronLeft,
  ChevronRight,
  MessageCircleX
} from 'lucide-react';
import DateSelector from './control-tower/DateSelector';
import DeliveryPointsList from './control-tower/DeliveryPointsList';
import OrdersList from './control-tower/OrdersList';
import DriversList from './control-tower/DriversList';
import UsersList from './control-tower/UsersList';
import MissingLocationModal from './control-tower/MissingLocationModal';
import DriverSelectionModal from './control-tower/DriverSelectionModal';
import ControlTowerMap from './control-tower/ControlTowerMap';
import SearchBar from './control-tower/SearchBar';
import DetailModal from './control-tower/DetailModal';
import UserDetailModal from './control-tower/modals/UserDetailModal';
import { NoResponseModal } from './control-tower/modals/NoResponseModal';

const API_URL = process.env.REACT_APP_API_URL;

function TorreDeControl({ setActiveComponent }) {
  // Date state
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showCalendar, setShowCalendar] = useState(false);
  
  // Data state
  const [deliveryPoints, setDeliveryPoints] = useState([]);
  const [orders, setOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [users, setUsers] = useState([]);
  const [missingLocationPoints, setMissingLocationPoints] = useState([]);
  
  // View state
  const [activeView, setActiveView] = useState('deliveries');
  const [showDeliveryMarkers, setShowDeliveryMarkers] = useState(true);
  const [showOrderMarkers, setShowOrderMarkers] = useState(false);
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  
  // Modal state
  const [showMissingLocationModal, setShowMissingLocationModal] = useState(false);
  const [showDriverSelectionModal, setShowDriverSelectionModal] = useState(false);
  const [selectedPointsForDriver, setSelectedPointsForDriver] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [selectedItemType, setSelectedItemType] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [noResponseCount, setNoResponseCount] = useState(null);
  const [showNoResponseModal, setShowNoResponseModal]   = useState(false);
  
  // List visibility state
  const [showLeftList, setShowLeftList] = useState(true);
  const [showRightList, setShowRightList] = useState(true);
  
  // Socket.IO ref
  const socketRef = useRef(null);

  // Memoize the marker click handler so it doesn't change on every render
  const handleItemClick = useCallback((id, type) => {
    if (type === 'user') {
      setSelectedUserId(id);
      // Clear delivery/order modals
      setSelectedItemId(null);
      setSelectedItemType(null);
    } else {
      setSelectedItemId(id);
      setSelectedItemType(type);
      // Clear user modal
      setSelectedUserId(null);
    }
  }, []);

  // Initialize with current date in Peru timezone
  useEffect(() => {
    const now = new Date();
    const utcTime = now.getTime();
    const peruOffset = -5 * 60 * 60 * 1000;
    const peruTime = new Date(utcTime + peruOffset);
    
    const year = peruTime.getUTCFullYear();
    const month = String(peruTime.getUTCMonth() + 1).padStart(2, '0');
    const day = String(peruTime.getUTCDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    console.log('Initializing with Peru date:', formattedDate);
    setSelectedDate(formattedDate);
  }, []);

  // Fetch data when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchDeliveryPoints();
      fetchOrders();
      fetchUsers();
    }
  }, [selectedDate]);

  // Setup Socket.IO connection with all listeners
  useEffect(() => {
    console.log('🔌 Initializing Socket.IO connection to:', API_URL);
    
    socketRef.current = io(API_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    // Connection events
    socketRef.current.on('connect', () => {
      console.log('✅ Socket.IO connected to Control Tower');
      console.log('Socket ID:', socketRef.current.id);
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('❌ Socket.IO connection error:', error);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('⚠️ Socket.IO disconnected:', reason);
    });

    // Delivery point updates
    socketRef.current.on('delivery_point_updated', (data) => {
      console.log('📦 Received delivery_point_updated:', data);
      
      setDeliveryPoints(prev => {
        const updated = prev.map(dp => dp.id === data.id ? { ...dp, ...data } : dp);
        
        // Update missing location points
        const missing = updated.filter(dp => !dp.latitude || !dp.longitude);
        setMissingLocationPoints(missing);
        
        return updated;
      });
    });

    // Order updates
    socketRef.current.on('order_updated', (payload) => {
      console.log('📦 Received order_updated:', payload);
      
      // Extract the nested order data
      if (payload.data) {
        setOrders(prev => 
          prev.map(order => 
            order.id === payload.order_id  // ← Use payload.order_id
              ? { ...order, ...payload.data }  // ← Use payload.data (the actual order)
              : order
          )
        );
        console.log(`✅ Updated order ${payload.order_id} in state`);
      } else {
        console.warn('⚠️ Received order_updated without data field:', payload);
      }
    });

    // Driver location updates
    socketRef.current.on('driver_location_updated', (data) => {
      console.log('🚗 Received driver_location_updated:', data);
      
      setDrivers(prev => {
        const existingIndex = prev.findIndex(d => d.id === data.id);
        
        if (existingIndex >= 0) {
          // Update existing driver
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            latitude: data.latitude,
            longitude: data.longitude,
          };
          return updated;
        } else {
          // Add new driver if not in list
          return [...prev, data];
        }
      });
    });

    // Cleanup
    return () => {
      console.log('🔌 Cleaning up Socket.IO connection');
      if (socketRef.current) {
        socketRef.current.off('connect');
        socketRef.current.off('connect_error');
        socketRef.current.off('disconnect');
        socketRef.current.off('delivery_point_updated');
        socketRef.current.off('order_updated');
        socketRef.current.off('driver_location_updated');
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Initial driver fetch on mount (only once)
  useEffect(() => {
    console.log('📍 Fetching initial driver locations...');
    fetchDriverLocations();
  }, []);

  useEffect(() => {
    setNoResponseCount(null);
  }, [selectedDate]);

  const fetchDeliveryPoints = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/delivery_points/${selectedDate}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setDeliveryPoints(response.data);
      
      const missing = response.data.filter(dp => !dp.latitude || !dp.longitude);
      setMissingLocationPoints(missing);
    } catch (error) {
      console.error('Error fetching delivery points:', error);
    }
  };

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/orders/${selectedDate}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/control_tower/users/${selectedDate}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setUsers(response.data);
      console.log('👥 Users fetched:', response.data.length);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchDriverLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/driver/location`);
      console.log('📍 Initial driver locations fetched:', response.data.length);
      setDrivers(response.data);
    } catch (error) {
      console.error('Error fetching driver locations:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
  };

  const toggleCalendar = () => {
    setShowCalendar(!showCalendar);
  };

  const toggleDeliveryMarkers = () => {
    setShowDeliveryMarkers(true);
    setShowOrderMarkers(false);
    setActiveView('deliveries');
    setIsDrawingMode(false);
  };

  const toggleOrderMarkers = () => {
    setShowDeliveryMarkers(false);
    setShowOrderMarkers(true);
    setActiveView('orders');
    setIsDrawingMode(false);
  };

  const toggleUserMarkers = () => {
    setShowDeliveryMarkers(false);
    setShowOrderMarkers(false);
    setActiveView('users');
    setIsDrawingMode(false);
  };

  const toggleDrawingMode = () => {
    setIsDrawingMode(!isDrawingMode);
  };

  const handleDrawingComplete = (selectedIds) => {
    setSelectedPointsForDriver(selectedIds);
    setShowDriverSelectionModal(true);
    setIsDrawingMode(false);
  };

  const handleDriverAssignment = async (driverId) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = activeView === 'deliveries' 
        ? '/api/control_tower/assign_driver_deliveries'
        : '/api/control_tower/assign_driver_orders';
      
      await axios.post(
        `${API_URL}${endpoint}`,
        {
          ids: selectedPointsForDriver,
          driver_id: driverId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (activeView === 'deliveries') {
        fetchDeliveryPoints();
      } else {
        fetchOrders();
      }

      setShowDriverSelectionModal(false);
      setSelectedPointsForDriver([]);
    } catch (error) {
      console.error('Error assigning driver:', error);
      alert('Error al asignar conductor');
    }
  };

  const handleToggleLeftList = () => {
    setShowLeftList(prev => !prev);
  };

  const handleToggleRightList = () => {
    setShowRightList(prev => !prev);
  };

  const handleDetailDeleted = (itemId, itemType) => {
    if (itemType === 'delivery') {
      setDeliveryPoints(prev => prev.filter(point => point.id !== itemId));
      setMissingLocationPoints(prev => prev.filter(point => point.id !== itemId));
    } else if (itemType === 'order') {
      setOrders(prev => prev.filter(order => order.id !== itemId));
    }
  };

  // Listen for custom events from map popups
  useEffect(() => {
    function handleModalEvent(event) {
      const { id, type } = event.detail;
      handleItemClick(id, type);
    }

    function handleOpenUserDetailModal(event) {
      const { userId } = event.detail;
      setSelectedItemId(null);
    setSelectedItemType(null);
      setSelectedUserId(userId);
      // UserDetailModal will open because selectedUserId is set
    }

    window.addEventListener('openDetailModal', handleModalEvent);
    window.addEventListener('openUserDetailModal', handleOpenUserDetailModal);
    return () => {
      window.removeEventListener('openDetailModal', handleModalEvent);
      window.removeEventListener('openUserDetailModal', handleOpenUserDetailModal);
    };
  }, [handleItemClick]);

  return (
    <div className="h-screen flex flex-col bg-slate-50 font-montserrat">
      {/* Fixed Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm px-4 py-3 flex items-center justify-between z-50 relative">
        <button
          onClick={() => setActiveComponent("OptionsAdmin")}
          className="flex items-center gap-2 text-slate-700 hover:text-[#B1336E] transition-colors duration-200 font-montserrat"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm sm:text-base">Volver</span>
        </button>

        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-[#B1336E] font-montserrat">Torre de Control</h1>
          
          {/* Status Indicators */}
          <div className="flex items-center gap-3">
            {/* Deliveries Group */}
            {deliveryPoints.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded-lg border border-slate-200">
                {/* Total Deliveries */}
                <div className="group relative">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5 text-[#B1336E]" />
                    <span className="text-xs font-bold text-slate-700 font-montserrat">{deliveryPoints.length}</span>
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                    Total Entregas
                  </div>
                </div>

                <div className="w-px h-4 bg-slate-300"></div>

                {/* Status Dots */}
                <div className="flex items-center gap-1">
                  {/* Entregado */}
                  {deliveryPoints.filter(dp => dp.status === 'entregado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'entregado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Entregados
                      </div>
                    </div>
                  )}

                  {/* En camino */}
                  {deliveryPoints.filter(dp => dp.status === 'encamino').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'encamino').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        En Camino
                      </div>
                    </div>
                  )}

                  {/* Pendiente */}
                  {deliveryPoints.filter(dp => dp.status === 'pending').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'pending').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Pendientes
                      </div>
                    </div>
                  )}

                  {/* Postergado */}
                  {deliveryPoints.filter(dp => dp.status === 'postergado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-orange-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'postergado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Postergados
                      </div>
                    </div>
                  )}

                  {/* Rechazado */}
                  {deliveryPoints.filter(dp => dp.status === 'rechazado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-red-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'rechazado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Rechazados
                      </div>
                    </div>
                  )}

                  {/* Cancelado */}
                  {deliveryPoints.filter(dp => dp.status === 'cancelado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {deliveryPoints.filter(dp => dp.status === 'cancelado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Cancelados
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Orders Group */}
            {orders.length > 0 && (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded-lg border border-slate-200">
                {/* Total Orders */}
                <div className="group relative">
                  <div className="flex items-center gap-1">
                    <Package className="w-3.5 h-3.5 text-[#B1336E]" />
                    <span className="text-xs font-bold text-slate-700 font-montserrat">{orders.length}</span>
                  </div>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                    Total Recojos
                  </div>
                </div>

                <div className="w-px h-4 bg-slate-300"></div>

                {/* Status Dots */}
                <div className="flex items-center gap-1">
                  {/* Completado */}
                  {orders.filter(o => o.status === 'completado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {orders.filter(o => o.status === 'completado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Completados
                      </div>
                    </div>
                  )}

                  {/* En camino */}
                  {orders.filter(o => o.status === 'encamino').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {orders.filter(o => o.status === 'encamino').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        En Camino
                      </div>
                    </div>
                  )}

                  {/* Pendiente */}
                  {orders.filter(o => o.status === 'pending').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {orders.filter(o => o.status === 'pending').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Pendientes
                      </div>
                    </div>
                  )}

                  {/* Cancelado */}
                  {orders.filter(o => o.status === 'cancelado').length > 0 && (
                    <div className="group relative">
                      <div className="flex items-center gap-0.5">
                        <div className="w-2 h-2 rounded-full bg-gray-500"></div>
                        <span className="text-[10px] font-medium text-slate-600 font-montserrat">
                          {orders.filter(o => o.status === 'cancelado').length}
                        </span>
                      </div>
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-900 text-white text-[10px] rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none font-montserrat">
                        Cancelados
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <SearchBar
            activeView={activeView}
            onResultClick={handleItemClick}
          />
          <button
            onClick={toggleCalendar}
            className="p-2 rounded-lg bg-slate-100 hover:bg-[#B1336E]/10 text-slate-700 hover:text-[#B1336E] transition-colors"
            title="Seleccionar fecha"
          >
            <Calendar className="w-5 h-5" />
          </button>

          <button
            onClick={() => setShowMissingLocationModal(true)}
            className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-700 hover:text-red-600 transition-colors relative"
            title="Puntos sin ubicación"
          >
            <AlertCircle className="w-5 h-5" />
            {missingLocationPoints.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold">
                {missingLocationPoints.length}
              </span>
            )}
          </button>

          <button
            onClick={() => setShowNoResponseModal(true)}
            className="p-2 rounded-lg bg-slate-100 hover:bg-orange-100 text-slate-700 hover:text-orange-600 transition-colors relative"
            title="Clientes sin respuesta"
          >
            <MessageCircleX className="w-5 h-5" />
            {noResponseCount > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orange-500" />
            )}
          </button>

          <button
            onClick={toggleDeliveryMarkers}
            className={`p-2 rounded-lg transition-colors ${
              activeView === 'deliveries'
                ? "bg-[#B1336E] text-white"
                : "bg-slate-100 hover:bg-[#B1336E]/10 text-slate-700 hover:text-[#B1336E]"
            }`}
            title="Mostrar entregas"
          >
            <MapPin className="w-5 h-5" />
          </button>

          <button
            onClick={toggleOrderMarkers}
            className={`p-2 rounded-lg transition-colors ${
              activeView === 'orders'
                ? "bg-[#B1336E] text-white"
                : "bg-slate-100 hover:bg-[#B1336E]/10 text-slate-700 hover:text-[#B1336E]"
            }`}
            title="Mostrar recojos"
          >
            <Package className="w-5 h-5" />
          </button>

          <button
            onClick={toggleUserMarkers}
            className={`p-2 rounded-lg transition-colors ${
              activeView === 'users'
                ? "bg-[#B1336E] text-white"
                : "bg-slate-100 hover:bg-[#B1336E]/10 text-slate-700 hover:text-[#B1336E]"
            }`}
            title="Mostrar usuarios"
          >
            <Users className="w-5 h-5" />
          </button>

          <button
            onClick={toggleDrawingMode}
            className={`p-2 rounded-lg transition-colors ${
              isDrawingMode
                ? "bg-[#67A43C] text-white"
                : "bg-slate-100 hover:bg-[#67A43C]/10 text-slate-700 hover:text-[#67A43C]"
            }`}
            title="Modo selección rectangular"
          >
            <Square className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Date Selector Modal */}
      {showCalendar && (
        <div className="fixed top-16 right-4 z-50">
          <DateSelector
            selectedDate={selectedDate}
            onDateChange={handleDateChange}
            showCalendar={showCalendar}
            onToggleCalendar={toggleCalendar}
          />
        </div>
      )}

      {/* Main Content - Fixed flex container */}
      <div className="flex-1 flex relative overflow-hidden">
        {/* Left Panel */}
        <div
          className={`transition-all duration-300 ease-in-out bg-white border-r border-slate-200 flex flex-col overflow-hidden relative flex-shrink-0 ${
            showLeftList ? "w-80" : "w-0"
          }`}
          style={{ zIndex: 20 }}
        >
          <div
            className={`w-80 h-full ${
              showLeftList ? "opacity-100" : "opacity-0"
            } transition-opacity duration-300`}
          >
            {activeView === "deliveries" ? (
              <DeliveryPointsList
                deliveryPoints={deliveryPoints}
                onItemClick={(id) => handleItemClick(id, 'delivery')}
              />
            ) : activeView === "orders" ? (
              <OrdersList
                orders={orders}
                onItemClick={(id) => handleItemClick(id, 'order')}
              />
            ) : activeView === "users" ? (
              <UsersList
                users={users}
                onItemClick={(id) => handleItemClick(id, 'user')}
              />
            ) : null}
          </div>
        </div>

        {/* Left Toggle Button */}
        <button
          onClick={handleToggleLeftList}
          className="fixed top-1/2 transform -translate-y-1/2 bg-white border border-slate-200 shadow-lg hover:shadow-xl p-2 transition-all duration-300 hover:bg-slate-50 rounded-lg"
          style={{
            left: showLeftList ? "320px" : "0px",
            zIndex: 1000,
            transition: "left 300ms ease-in-out",
          }}
          title={showLeftList ? "Ocultar lista" : "Mostrar lista"}
        >
          {showLeftList ? (
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          )}
        </button>

        {/* Map Container */}
        <div
          className="flex-1 relative bg-slate-100 transition-all duration-300 ease-in-out"
          style={{ zIndex: 0 }}
        >
          <ControlTowerMap
            deliveryPoints={deliveryPoints}
            orders={orders}
            drivers={drivers}
            showDeliveryMarkers={showDeliveryMarkers}
            showOrderMarkers={showOrderMarkers}
            isDrawingMode={isDrawingMode}
            onDrawingComplete={handleDrawingComplete}
            onMarkerClick={handleItemClick}
            sidebarState={`${showLeftList ? '1' : '0'}-${showRightList ? '1' : '0'}`}
          />
        </div>

        {/* Right Panel */}
        <div
          className={`transition-all duration-300 ease-in-out bg-white border-l border-slate-200 flex flex-col overflow-hidden relative flex-shrink-0 ${
            showRightList ? "w-80" : "w-0"
          }`}
          style={{ zIndex: 20 }}
        >
          <div
            className={`w-80 h-full ${
              showRightList ? "opacity-100" : "opacity-0"
            } transition-opacity duration-300`}
          >
            <DriversList
              drivers={drivers}
              deliveryPoints={deliveryPoints}
              orders={orders}
              activeView={activeView}
            />
          </div>
        </div>

        {/* Right Toggle Button */}
        <button
          onClick={handleToggleRightList}
          className="fixed top-1/2 transform -translate-y-1/2 bg-white border border-slate-200 shadow-lg hover:shadow-xl p-2 transition-all duration-300 hover:bg-slate-50 rounded-lg"
          style={{
            right: showRightList ? "320px" : "0px",
            zIndex: 1000,
            transition: "right 300ms ease-in-out",
          }}
          title={showRightList ? "Ocultar lista" : "Mostrar lista"}
        >
          {showRightList ? (
            <ChevronRight className="w-4 h-4 text-slate-600" />
          ) : (
            <ChevronLeft className="w-4 h-4 text-slate-600" />
          )}
        </button>
      </div>

      {/* Modals */}
      {showMissingLocationModal && (
        <MissingLocationModal
          missingPoints={missingLocationPoints}
          onClose={() => setShowMissingLocationModal(false)}
        />
      )}

      {showNoResponseModal && (
        <NoResponseModal
          selectedDate={selectedDate}
          onCountLoaded={setNoResponseCount}
          onClose={() => setShowNoResponseModal(false)}
        />
      )}

      {showDriverSelectionModal && (
        <DriverSelectionModal
          drivers={drivers}
          onSelect={handleDriverAssignment}
          onClose={() => {
            setShowDriverSelectionModal(false);
            setSelectedPointsForDriver([]);
          }}
          selectedCount={selectedPointsForDriver.length}
        />
      )}

      {/* Delivery/Order Detail Modal */}
      {selectedItemId && selectedItemType && (
        <DetailModal
          itemId={selectedItemId}
          itemType={selectedItemType}
          onDeleted={handleDetailDeleted}
          onClose={() => {
            setSelectedItemId(null);
            setSelectedItemType(null);
          }}
        />
      )}

      {/* User Detail Modal */}
      {selectedUserId && (
        <UserDetailModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
      
    </div>
  );
}

export default TorreDeControl;
